// ENERGY v2 — one wallet, as a sheet over the list (?wallet=<id>) (UI rebuild batch 4, adopted by the energy lane).
// Astra: "put configuration behind each wallet". The wallet's facts, ONE A LINE (his words, 1 Nov 2026: "check everything where it
// applies"): the address with Copy and the explorer, when it was read, its energy and what it needs, TRX, the standing figure and
// how much of it is in place, what is rented and when it ends; then each warning in its box — the desk's alarms about it, the
// register's no, an activation that went wrong, why renting is off — and the wallet's two doors: Rent energy (the rent page, this
// wallet chosen) and Settings (full access only). ONE COPY: the words and the rule that holds renting back are screens/Energy.tsx's
// (useTopUpOff: never while an order that got no answer waits to be sent again).
import { Link } from 'react-router-dom';
import type { EnergyAlarm, EnergyStatus, EnergyWallet } from '../../../lib/api';
import { fmtStamp } from '../../../lib/format';
import { DetailSheet, Fact, StateLine, TAP } from '../kit';
import { CopyRef } from '../../CopyRef';
import { Pill } from '../../Pill';
import { AlarmLine, EnergyFigure, EnergyNeed, EnergyNow, Explorer, LifeTag, TrxFigure, V2_SLABS, hasReading } from './parts';
import {
  STATE_TONE, STATE_WORD, activationLine, alarmRentalsOf, endsIn, holdBack, jumpWordOf, mask, nameOf, readAgo, registerLine, registerOf, secondsUntil, trx,
  useTopUpOff, walletSeat,
} from '../../../screens/Energy';

export function WalletSheet({ w, d, canChange, alarms, onClose }: {
  w: EnergyWallet; d: EnergyStatus; canChange: boolean;
  /** The desk's alarms about this wallet — in full here, whether or not the list's row carries them. */
  alarms: readonly EnergyAlarm[];
  onClose: () => void;
}) {
  const off = useTopUpOff(d, w);
  const seat = walletSeat(d, w);
  const s = registerOf(d, w);
  const activation = activationLine(w, d);
  const standing = Number(w.standingEnergy) > 0;
  const rented = Number(w.rentedEnergy) > 0;
  return (
    <DetailSheet title={<span className="break-words">{nameOf(d, w)}</span>} onClose={onClose}
      footer={
        <div className="flex flex-col-reverse gap-2 lg:flex-row lg:justify-end">
          {canChange && <Link to={`/energy/settings/${w.id}`} className="btn-secondary w-full px-4 text-body lg:w-auto">Settings</Link>}
          {off === null
            ? <Link to={`/energy/rent?wallet=${w.id}`} className="btn-primary w-full lg:w-auto lg:px-6">Rent energy</Link>
            : <button type="button" className="btn-primary w-full disabled:opacity-50 lg:w-auto lg:px-6" disabled title={off}>Rent energy</button>}
        </div>
      }>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Pill tone={seat.tone}>{seat.word}</Pill>
        <LifeTag w={w} d={d} />
      </div>
      <div>
        <Fact label="Address">
          <span className="inline-flex items-center gap-1.5"><CopyRef value={w.address} label={mask(w.address)} /><Explorer network={d.network} address={w.address} /></span>
        </Fact>
        <Fact label="Read">{w.observedAt ? <span title={fmtStamp(w.observedAt)}>{readAgo(w.observedAt)}</span> : <span className="text-tg-hint">—</span>}</Fact>
        <Fact label="Available"><EnergyNow w={w} /></Fact>
        {/* The chain's own limit for the wallet — what it can hold — beside what it has (WalletEnergy's "of …" on today's screen). */}
        {hasReading(w) && w.energyLimit !== null && <Fact label="Energy limit"><EnergyFigure n={w.energyLimit} /></Fact>}
        <Fact label="Needs"><EnergyNeed w={w} /></Fact>
        <Fact label="TRX"><TrxFigure value={w.status === 'retired' || w.trxBalanceSun === null ? '—' : trx(w.trxBalanceSun)} /></Fact>
        <Fact label="Standing">{standing ? <EnergyFigure n={w.standingEnergy} /> : <span className="text-tg-hint">—</span>}</Fact>
        {standing && <Fact label="In place"><EnergyFigure n={w.standingCovered} tone={Number(w.standingCovered) < Number(w.standingEnergy) ? 'text-warn' : ''} /></Fact>}
        <Fact label="Rented">{rented ? <EnergyFigure n={w.rentedEnergy} /> : <span className="text-tg-hint">—</span>}</Fact>
        {rented && w.nextExpiry && <Fact label="Ends"><span title={endsIn(secondsUntil(w.nextExpiry, d.asOf))}>{fmtStamp(w.nextExpiry)}</span></Fact>}
        {/* An order on its way, or one with no answer: its state, a fact of its own — unless the tag above already says it (a wallet
            whose chain read failed has that tag, and its accepted order would go unsaid). */}
        {w.unresolved && seat.word !== STATE_WORD[w.unresolved.state] && (
          <Fact label="Order"><Pill tone={STATE_TONE[w.unresolved.state]}>{STATE_WORD[w.unresolved.state]}</Pill></Fact>
        )}
      </div>
      <div className="mt-4 space-y-2">
        {/* Each alarm's way out, in its box: the rentals of this wallet it names (an order with no answer: Settle #N). */}
        {alarms.map((a) => {
          const ids = alarmRentalsOf(a, d, w.id);
          return <AlarmLine key={a.key} a={a} d={d}
            action={ids.length === 0 ? undefined : <>{ids.map((id) => <Link key={id} className={TAP} to={`/energy?rental=${id}`}>{jumpWordOf(d, canChange, id)} #{id}</Link>)}</>} />;
        })}
        {s.kind === 'not_live' && <StateLine tone="warn" className={V2_SLABS.warn} more={holdBack(s) ?? undefined}>{registerLine(s)}</StateLine>}
        {activation && <StateLine tone={activation.tone} className={V2_SLABS[activation.tone]} more={activation.tip ?? undefined}>{activation.text}</StateLine>}
        {/* The reason renting is off — on the screen, not on hover: a phone has no hover (a review, 18 Oct 2026). */}
        {off !== null && <StateLine tone="warn" className={V2_SLABS.warn} more={off}>Renting is off for this wallet.</StateLine>}
      </div>
    </DetailSheet>
  );
}
